import { JSX } from 'react';
import { twMerge } from '../../utils/tw-merge';
import Anchor from '../anchor/anchor';

interface ButtonBase {
    label: string;
}

interface BaseProps {
    variant?: 'primary' | 'secondary' | 'ghost' | 'white' | 'white-ghost';
    tiny?: boolean;
}

interface AnchorButton extends ButtonBase {
    href: string;
}

type RegularButton = ButtonBase;

type Button = RegularButton | AnchorButton;

type AnchorButtonProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & AnchorButton & BaseProps;

type RegularButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & RegularButton & BaseProps;

type ButtonProps = AnchorButtonProps | RegularButtonProps;

function isAnchorButtonProps(props: ButtonProps): props is AnchorButtonProps {
    return 'href' in props;
}

function Button(props: ButtonProps): JSX.Element {
    const { tiny, variant, ...restProps } = props;
    const selectedVariant = props.variant ?? 'primary';
    const tinyClasses = `py-0 px-[15px] ${variant === 'ghost' ? 'border-[2px]' : ''}`;
    const buttonBaseClasses =
        'py-2 px-5 text-14/24 cursor-pointer rounded-[20px] font-extrabold tracking-widest uppercase disabled:opacity-30';
    const buttonVariantClasses = {
        primary:
            'bg-background-default-secondary text-text-default-primary-light hover:bg-background-hover-primary focus:bg-background-hover-primary active:bg-background-pressed-primary',
        secondary:
            'bg-background-default-primary text-text-default-primary-light hover:bg-background-hover-primary focus:bg-background-hover-primary active:bg-background-pressed-primary',
        ghost: 'bg-transparent border-solid border-[1px] border-border-default-primary text-text-titles-secondary hover:text-text-hover-primary hover:border-border-hover-primary focus:text-text-hover-primary focus:border-border-hover-primary active:text-text-pressed-primary active:border-border-pressed-primary',
        white: 'bg-background-white-primary text-text-default-primary hover:bg-background-white-primary-hover focus:bg-background-white-primary-hover active:bg-background-white-primary-pressed disabled:bg-background-white-primary-disabled',
        'white-ghost':
            'bg-transparent text-text-default-primary-light border-solid border-[1px] border-border-white-primary disabled:border-background-white-primary-disabled disabled:text-background-white-primary-disabled',
    };

    const className = twMerge(
        buttonBaseClasses,
        buttonVariantClasses[selectedVariant],
        tiny && tinyClasses,
        props.className,
    );

    if (isAnchorButtonProps(restProps)) {
        return <AnchorButton {...restProps} className={className} />;
    }

    return <RegularButton {...(restProps as RegularButtonProps)} className={className} />;
}

function AnchorButton({ href, label, ...rest }: AnchorButtonProps): JSX.Element {
    return (
        <Anchor {...rest} href={href}>
            {label}
        </Anchor>
    );
}

function RegularButton({ label, type, ...rest }: RegularButtonProps): JSX.Element {
    return (
        <button {...rest} type={type ?? 'button'}>
            {label}
        </button>
    );
}

export default Button;
